console.log('Client Mediland chargé');

// fetch('/admin/patients')
//   .then(res => res.json())
//   .then(data => console.log('Patients:', data));

import * as renderer from '@shared/renderer'
import { createLanguageSwitcher, persistLocale, resolveLocale, type Locale } from '@shared/i18n';
import { createGameTranslator } from './i18n';

let locale:Locale = resolveLocale();
persistLocale(locale, false);
const t = createGameTranslator(locale);
renderer.setTextTranslator(t);

type MedilandRuntimeConfig = {
    mediaBaseUrl?: string;
    mediaBaseUrls?: Partial<Record<Locale, string>>;
    bypassCode?: string;
    anonymous?: boolean;
    bypassArrow?: boolean;
};

const runtimeConfig = (window as Window & {
    __MEDILAND_CONFIG__?: MedilandRuntimeConfig;
}).__MEDILAND_CONFIG__ ?? {};

const BYPASS_CODE = runtimeConfig.bypassCode?.trim() || '246';
const ANONYMOUS = runtimeConfig.anonymous ?? true;
const BYPASS_ARROW = runtimeConfig.bypassArrow ?? true;

let videoElement:HTMLVideoElement|undefined;
let quizElement:HTMLElement|undefined;
let overlayElement:HTMLElement|undefined;
let controlLayer:HTMLElement|undefined;
let titleOverlayElement:HTMLElement|undefined;
let titleElement:HTMLElement|undefined;
let videoMentionOverlayElement:HTMLElement|undefined;
let videoMentionLabelElement:HTMLElement|undefined;
let videoMentionTitleElement:HTMLElement|undefined;
let videoMentionTextElement:HTMLElement|undefined;

const TITLE_DISPLAY_DURATION = 7*1000;
const TITLE_REMINDER_INTERVAL = 20*1000;
const TITLE_AUTO_HIDE_ENABLED = false; // Toggle if you ever want automatic hide after TITLE_DISPLAY_DURATION
const TITLE_AUTO_REMIND_ENABLED = false; // Toggle to bring back the periodic reminder behaviour

let titleReminderInterval:number|undefined;
let titleHideTimeout:number|undefined;
let titleConcealTimeout:number|undefined;
let activeTitleCue:TextTrackCue|undefined;
let lastTitleContent:any;
let titleSuppressedByImage = false;
let videoMentionConcealTimeout:number|undefined;
let activeVideoMentionKey:string|undefined;

type TimedVideoMention = {
    key:string;
    startAtMs:number;
    endAtMs:number;
    label:string;
    title:string;
    text:string;
};

let currentVideoMentions:TimedVideoMention[] = [];

const START_WINDOW_MENTIONS:TimedVideoMention[] = [
    {
        key: 'has-start-window',
        startAtMs: 13_000,
        endAtMs: 19_000,
        label: 'Informations',
        title: '',
        text: 'les informations données lors de ce parcours clinique virtuel sont issues des recommandations pour la chirurgie de l’obésité publiée par Haute Autorité de Santé France en 2024',
    },
];

const symptomNodes = new Map<string, HTMLElement>();
const imageNodes = new Map<string, HTMLElement>();

let activeInstance = 'mediland';

function appendPath(baseUrl:string, filename:string) {
    const normalizedBaseUrl = baseUrl.replace(/\/+$/, '');
    const normalizedFilename = filename.replace(/^\/+/, '');
    return `${normalizedBaseUrl}/${normalizedFilename}`;
}

function instancePath(filename:string, instance:string=activeInstance) {
    return appendPath(`/instances/${instance}`, filename);
}

function videoPath(filename:string) {
    const mediaBaseUrl = runtimeConfig?.mediaBaseUrls?.[locale]?.trim() || runtimeConfig?.mediaBaseUrl?.trim();
    return mediaBaseUrl
        ? appendPath(mediaBaseUrl, filename)
        : instancePath(filename, activeInstance);
}

function localizedContentPath(path:string) {
    if (path.startsWith('/assets/boards/')) {
        return path.replace('/assets/boards/', `/assets/content/${locale}/boards/`);
    }
    return path;
}

function triggerFadeIn(element: HTMLElement) {
    element.classList.remove('pb-fade-in');
    void element.offsetWidth;
    element.classList.add('pb-fade-in');
    element.addEventListener('animationend', () => {
        element.classList.remove('pb-fade-in');
    }, { once: true });
}

function populateSymptomCard(card: HTMLElement, items: any[]) {
    const titleEl = card.querySelector('.pb-title') as HTMLElement | null;
    const listEl = card.querySelector('.pb-list') as HTMLElement | null;
    if (!titleEl || !listEl) return;

    let computedTitle = '';
    for (const item of items) {
        if (item.title) computedTitle = item.title;
    }
    titleEl.textContent = computedTitle;

    listEl.innerHTML = '';
    for (const item of items) {
        const entry = document.createElement('div');
        entry.innerHTML = item.text || '';
        listEl.appendChild(entry);
    }
}

function createSymptomCard(items: any[], key: string) {
    const card = document.createElement('div');
    card.className = 'pb-card';
    card.dataset.symptomGroup = key;

    const title = document.createElement('div');
    title.className = 'pb-title';
    card.appendChild(title);

    const list = document.createElement('div');
    list.className = 'pb-list';
    card.appendChild(list);

    populateSymptomCard(card, items);
    return card;
}

function updateFigureElement(figureEl: HTMLElement, data: any) {
    let img = figureEl.querySelector('img') as HTMLImageElement | null;
    if (!img) {
        img = document.createElement('img');
        figureEl.insertBefore(img, figureEl.firstChild);
    }
    const localizedSrc = data.src ? localizedContentPath(data.src) : '';
    if (localizedSrc && img.getAttribute('src') !== localizedSrc) {
        img.src = localizedSrc;
    }
    img.alt = data.caption || '';

    figureEl.style.left = '';
    figureEl.style.right = '';
    if (data.anchor === 'right') {
        figureEl.style.right = '2rem';
    } else if (data.anchor === 'left') {
        figureEl.style.left = '2rem';
    }

    let caption = figureEl.querySelector('figcaption') as HTMLElement | null;
    if (data.caption) {
        if (!caption) {
            caption = document.createElement('figcaption');
            figureEl.appendChild(caption);
        }
        caption.textContent = data.caption;
    } else if (caption) {
        caption.remove();
    }
}

function createFigureElement(data: any, key: string) {
    const figureEl = document.createElement('figure');
    figureEl.className = 'pb-figure';
    figureEl.dataset.imageKey = key;
    updateFigureElement(figureEl, data);
    return figureEl;
}

function hideTitle(immediate = false) {
    if (!titleOverlayElement) return;

    if (titleHideTimeout) {
        window.clearTimeout(titleHideTimeout);
        titleHideTimeout = undefined;
    }
    if (titleConcealTimeout) {
        window.clearTimeout(titleConcealTimeout);
        titleConcealTimeout = undefined;
    }

    if (immediate) {
        titleOverlayElement.classList.remove('title-visible');
        titleOverlayElement.classList.add('hidden');
        return;
    }

    titleOverlayElement.classList.remove('title-visible');
    titleConcealTimeout = window.setTimeout(() => {
        titleOverlayElement?.classList.add('hidden');
        titleConcealTimeout = undefined;
    }, 650);
}

function resetTitle(options:{ immediate?:boolean; clearContent?:boolean } = {}) {
    if (titleReminderInterval) {
        window.clearInterval(titleReminderInterval);
        titleReminderInterval = undefined;
    }
    if (titleHideTimeout) {
        window.clearTimeout(titleHideTimeout);
        titleHideTimeout = undefined;
    }
    if (titleConcealTimeout) {
        window.clearTimeout(titleConcealTimeout);
        titleConcealTimeout = undefined;
    }

    if (options.immediate) {
        hideTitle(true);
    }
    if (options.clearContent) {
        lastTitleContent = undefined;
        titleElement?.replaceChildren();
    }
    titleSuppressedByImage = false;
}

function showTitle(content?:any, options:{ remind?:boolean } = {}) {
    if (!titleOverlayElement || !titleElement) return;

    const remind = options.remind ?? true;

    if (titleHideTimeout) {
        window.clearTimeout(titleHideTimeout);
        titleHideTimeout = undefined;
    }
    if (titleConcealTimeout) {
        window.clearTimeout(titleConcealTimeout);
        titleConcealTimeout = undefined;
    }
    if (remind && titleReminderInterval) {
        window.clearInterval(titleReminderInterval);
        titleReminderInterval = undefined;
    }

    if (content !== undefined) {
        lastTitleContent = content;
    }

    if (lastTitleContent !== undefined) {
        titleElement.replaceChildren(renderer.render(lastTitleContent));
    } else {
        titleElement.replaceChildren();
    }

    titleOverlayElement.classList.remove('hidden');
    titleOverlayElement.classList.remove('title-visible');
    void titleOverlayElement.offsetWidth;
    titleOverlayElement.classList.add('title-visible');

    if (TITLE_AUTO_HIDE_ENABLED) {
        titleHideTimeout = window.setTimeout(() => {
            titleHideTimeout = undefined;
            hideTitle();
        }, TITLE_DISPLAY_DURATION);
    }

    if (TITLE_AUTO_REMIND_ENABLED && remind) {
        titleReminderInterval = window.setInterval(() => {
            showTitle(undefined, { remind: false });
        }, TITLE_REMINDER_INTERVAL);
    }
}

function hideVideoMention(immediate = false) {
    if (!videoMentionOverlayElement) return;

    if (videoMentionConcealTimeout) {
        window.clearTimeout(videoMentionConcealTimeout);
        videoMentionConcealTimeout = undefined;
    }

    if (immediate) {
        videoMentionOverlayElement.classList.remove('mention-visible');
        videoMentionOverlayElement.classList.add('hidden');
        return;
    }

    videoMentionOverlayElement.classList.remove('mention-visible');
    videoMentionConcealTimeout = window.setTimeout(() => {
        videoMentionOverlayElement?.classList.add('hidden');
        videoMentionConcealTimeout = undefined;
    }, 320);
}

function showVideoMention(mention:TimedVideoMention) {
    if (!videoMentionOverlayElement || !videoMentionLabelElement || !videoMentionTitleElement || !videoMentionTextElement) {
        return;
    }

    if (videoMentionConcealTimeout) {
        window.clearTimeout(videoMentionConcealTimeout);
        videoMentionConcealTimeout = undefined;
    }

    videoMentionLabelElement.textContent = t(mention.label);
    videoMentionTitleElement.textContent = t(mention.title);
    videoMentionTextElement.textContent = t(mention.text);

    videoMentionOverlayElement.classList.remove('hidden');
    videoMentionOverlayElement.classList.remove('mention-visible');
    void videoMentionOverlayElement.offsetWidth;
    videoMentionOverlayElement.classList.add('mention-visible');
}

function syncVideoMention() {
    if (!videoElement) return;

    const nowMs = Math.round(videoElement.currentTime * 1000);
    const activeMention = currentVideoMentions.find((mention) => nowMs >= mention.startAtMs && nowMs < mention.endAtMs);

    if (!activeMention) {
        if (activeVideoMentionKey !== undefined) {
            activeVideoMentionKey = undefined;
            hideVideoMention();
        }
        return;
    }

    if (activeVideoMentionKey === activeMention.key) return;

    activeVideoMentionKey = activeMention.key;
    showVideoMention(activeMention);
}

function setVideoMentions(mentions:TimedVideoMention[] = []) {
    currentVideoMentions = [...mentions].sort((a, b) => a.startAtMs - b.startAtMs);
    activeVideoMentionKey = undefined;
    hideVideoMention(true);
    syncVideoMention();
}

let progressBarContainer:HTMLElement|undefined;
let progressBarElement:HTMLElement|undefined;
let progressTextElement:HTMLElement|undefined;

function updateProgress(progress:number) {
    progressBarContainer!.classList.remove('hidden');
    progressBarElement!.style.width = `${(100*progress/70).toFixed(0)}%`;
    progressTextElement!.innerText = t('Question n° {current} / {total}', { current: progress, total: 70 });
}

function renderLayout() {
    let pauseIconElement;
    document.getElementsByTagName('body')![0].appendChild(renderer.render(
        { className: 'app-container', children: [
            { className: 'stage-wrapper', children: [
                { className: 'video-layer', children: { tag: 'video', id: 'video', playsinline: true, hook: (e) => {
                    videoElement = e as HTMLVideoElement;
                    videoElement.controls = false;
                    videoElement.autoplay = false;
                    videoElement.muted = false;
                    videoElement.loop = false;
                    videoElement.addEventListener('timeupdate', syncVideoMention);
                    videoElement.addEventListener('seeking', syncVideoMention);
                    videoElement.addEventListener('seeked', syncVideoMention);
                    videoElement.addEventListener('ended', () => {
                        activeVideoMentionKey = undefined;
                        hideVideoMention(true);
                    });
                } } },
                { id:'paperboard', className:'paperboard', children: [
                    { tag:'section', "data-layer":"symptoms"},
                    { tag:'section', "data-layer":"image"},
                ]},
                { className: 'quiz-layer', children: renderQuiz() },
                { id: 'controls-layer', className: 'hidden', children: [
                    { className: 'control-buttons', children: [
                      { tag: 'button', className: 'control-btn pause-btn', children: [{ tag: 'i', className: 'fa-solid fa-pause', hook: (e) => { pauseIconElement = e;} }], onclick: (e:Event) => {
                            if (pauseIconElement!.classList.contains('fa-pause')) {
                                pauseIconElement!.classList.remove('fa-pause');
                                pauseIconElement!.classList.add('fa-play');
                                videoElement!.pause();
                            } else {
                                pauseIconElement!.classList.remove('fa-play');
                                pauseIconElement!.classList.add('fa-pause');
                                videoElement!.play();
                            }
                        } },
                        { tag: 'button', className: 'control-btn skip-btn small-font', children: "-10", onclick: () => { 
                            videoElement!.currentTime = Math.max(0, Math.min(videoElement!.currentTime - 10, videoElement!.duration));
                        } },
                        { tag: 'button', className: 'control-btn skip-btn small-font', children: "+10", onclick: () => {
                            videoElement!.currentTime = Math.max(0, Math.min(videoElement!.currentTime + 10, videoElement!.duration));
                        } },
                        BYPASS_ARROW ? { tag: 'button', className: 'control-btn skip-btn', children: { tag: 'i', className: 'fa-solid fa-arrow-right' }, onclick: next } : '',
                    ]},
                ], hook: (e) => { controlLayer = e; } },
                { id: 'video-mention-overlay', className:'hidden', children: [
                    { className: 'video-mention-card', children: [
                        { className: 'video-mention-label', children: 'Mention HAS', hook: (e) => { videoMentionLabelElement = e; } },
                        { className: 'video-mention-copy', children: [
                            { className: 'video-mention-title', children: 'Texte client a renseigner', hook: (e) => { videoMentionTitleElement = e; } },
                            { className: 'video-mention-text', children: 'Remplacer ce texte par la mention HAS fournie par le client.', hook: (e) => { videoMentionTextElement = e; } },
                        ]},
                    ]},
                ], hook: (e) => { videoMentionOverlayElement = e; } },
                { id: 'title-overlay', className:'hidden', children: { className: 'title', children: "Médecin spécialiste de l'obésité", hook(e) { titleElement = e }  }, hook(e) { titleOverlayElement = e } },
                { id: 'overlay-layer', hook(e) { overlayElement = e } },
                { className: 'progress-container hidden', children: [
                    { className: 'progress-bar', style: { width: "0%" }, hook: (e) => { progressBarElement = e } },
                    { tag: 'span', className: 'progress-text', children: "Progression", hook(e) { progressTextElement = e }  }
                ], hook(e) { progressBarContainer=e; }}, 
                // { id: 'instance-selector', children: [
                //     { className:'instance', children: { tag: 'img', src: instancePath('logo.png', 'mediland') }, onclick: () => {
                //         activeInstance = 'mediland';
                //         document.getElementById('instance-selector')?.classList.add('hidden');
                //         next();
                //     } },
                //     // { className:'instance', children: { tag: 'img', src: instancePath('logo.png', 'chu') }, onclick: () => {
                //     //     activeInstance = 'chu';
                //     //     document.getElementById('instance-selector')?.classList.add('hidden');
                //     //     next();
                //     // } },
                // ]}
            ]},
        ]}
    ));
}

function hideExpressions() {
    if (happyTimer) clearTimeout( happyTimer );
    for (const element of document.querySelectorAll('.doctor-expression')) {
        element.classList.add('hidden');
    }
}

function renderQuiz() {
    return { className: 'quiz-screen hidden', children: [
        { className: 'quiz-background' },
        { className: 'quiz-content', children: [

            { className: 'question', children: [
                
                { tag: 'p', id:'question-text', className: 'question-text', children: [
                    { tag: 'span', className: 'question-number', children: "64. " },
                    "L’opération de chirurgie bariatrique se déroule toujours sous anesthésie générale."
                ] },
                { tag: 'img', src: '', className: 'question-image' },

                { id: 'quiz-feedback', className: 'quiz-feedback', children: [
                    { className: 'answer', children: 'Vrai.' },
                    { className: 'explanation', children: 'Parce que blabla.' }
                ]}
            ]},

            { className: 'quiz-buttons', children: [
                { tag: 'button', id: 'true-button', className: 'answer-button true', children: "Vrai" },
                { tag: 'button', id: 'false-button', className: 'answer-button false', children: "Faux" },
                { id: 'next-button', className: 'next-button hidden' },
            ] },
            

            { tag: 'img', id:'hippocrate-neutral', src:'assets/ui/hippocrate-neutral.png', className: 'doctor-expression neutral hidden' },
            { tag: 'img', id:'hippocrate-happy', src:'assets/ui/hippocrate-right.png', className: 'doctor-expression happy hidden' },
            { tag: 'img', id:'hippocrate-sad', src:'assets/ui/hippocrate-wrong.png', className: 'doctor-expression sad hidden' },
            

            // Should not be here ? (bypass quiz)
            (BYPASS_ARROW) ?
            { className: 'control-buttons', children: [
                { tag: 'button', className: 'control-btn skip-btn', children: { tag: 'i', className: 'fa-solid fa-arrow-right' }, onclick: skipActiveQuiz },
            ]} : ''
            //

        ] },
    ], hook: (e:HTMLElement) => { quizElement = e; }}
}

type QuizQuestion = {
    id: number
    question: string
    answer: boolean
    explanation?: renderer.RenderNode,
    media?: string[],
    note?: string
}

type Quiz = QuizQuestion[];

const successAudio = new Audio('/assets/ui/success.mp3');
successAudio.load();
const failAudio = new Audio('/assets/ui/fail.mp3');
failAudio.load();
const applauseAudio = new Audio('/assets/ui/applause.wav');
applauseAudio.load();

async function loadQuiz(type:string):Promise<Quiz> {
    const res = await fetch(`/assets/content/${locale}/quiz/quiz_${type}.json`);
    return res.json();
}

function displayQuestion(question:QuizQuestion) {
    document.getElementById('question-text')!.replaceWith(renderer.render(
        { tag: 'p', id:'question-text', className: 'question-text', children: [
            { tag: 'span', className: 'question-number', children: `${question.id}.` },
            question.question
        ] },
    ));

    document.getElementById('quiz-feedback')!.replaceWith(renderer.render(
        { id: 'quiz-feedback', className: 'quiz-feedback hidden', children: [
            { className: 'answer', children: question.answer ? 'Vrai' : 'Faux' },
            { className: 'explanation', children: question.explanation },
            { className: 'media', style: { display: question.media ? 'flex' : 'none' }, children: { tag: 'img', src: question.media ? `/content/${locale}/media/${question.media[0]}` : '' } }
        ]}
    ));

}
let currentAudio:HTMLAudioElement|undefined;
let quizActive = false;
let quizCancelled = false;
let cancelQuizWait: (() => void)|undefined;

function stopQuizAudio() {
    currentAudio?.pause();
    if (currentAudio) currentAudio.currentTime = 0;
    currentAudio = undefined;
}

function hideQuiz() {
    stopQuizAudio();
    hideExpressions();
    quizElement?.classList.add('hidden');
    controlLayer?.classList.remove('hidden');
}

async function skipActiveQuiz() {
    if (!quizActive) return;
    quizCancelled = true;
    cancelQuizWait?.();
    quizActive = false;
    hideQuiz();
    await advanceSequence(true);
}

function playAudioForQuestion(question:QuizQuestion) {
    currentAudio?.pause();
    currentAudio = new Audio(`/assets/content/${locale}/quiz/all_quizz_${question.id}.mp3`);
    currentAudio.play();
}
function playAudioForAnswer(question:QuizQuestion) {
    currentAudio?.pause();
    currentAudio = new Audio(`/assets/content/${locale}/quiz/all_quizz_${question.id}a.mp3`);
    currentAudio.play();
}

async function playQuiz(type:string, begin?:number, end?:number) {
    quizActive = true;
    quizCancelled = false;

    try {
        const quiz = await loadQuiz(type);
        if (quizCancelled) return;
        resetTitle({ immediate: true, clearContent: true });
        controlLayer?.classList.add('hidden');
        quizElement?.classList.remove('hidden');

        // Start with a question (bypass issue)
        {
            hideExpressions();
            document.getElementById('true-button')?.classList.remove('hidden');
            document.getElementById('false-button')?.classList.remove('hidden');
            document.getElementById('next-button')?.classList.add('hidden');
            document.getElementById('quiz-feedback')?.classList.add('hidden');
        }

        document.getElementById('hippocrate-neutral')?.classList.remove('hidden');

        for (let i=begin??0; i<(end??quiz.length); i++) {
            const question = quiz[i];
            if (progress>=question.id) continue;

            displayQuestion(question);

            document.getElementById('true-button')!.onclick = async () => {
                await resolveAnswer(question, true);
            }
            document.getElementById('false-button')!.onclick = async () => {
                await resolveAnswer(question, false);
            }
            playAudioForQuestion(question);

            const continueQuiz = await waitForNextClick();
            if (!continueQuiz) return;
            document.getElementById('true-button')?.classList.remove('hidden');
            document.getElementById('false-button')?.classList.remove('hidden');
            document.getElementById('next-button')?.classList.add('hidden');
            document.getElementById('quiz-feedback')?.classList.add('hidden');
        }
    } finally {
        cancelQuizWait = undefined;
        hideQuiz();
        quizActive = false;
    }

    if (!quizCancelled) await advanceSequence(true);
}

async function resolveAnswer(question:QuizQuestion, answer:boolean) {
    if (answer == question.answer) {
        await rightAnswer(question);
        document.getElementById('true-button')?.classList.add('hidden');
        document.getElementById('false-button')?.classList.add('hidden');
        document.getElementById('next-button')?.classList.remove('hidden');
        document.getElementById('quiz-feedback')?.classList.remove('hidden');
        playAudioForAnswer(question);
    } else {
        await wrongAnswer();
    }
}
let happyTimer:any;
async function rightAnswer(question:QuizQuestion) {
    
    fetch(`/patient/${patientCode}/progress`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({ progress: question.id })
    })
    progress = Math.max(progress, question.id);
    updateProgress(progress);
    
    successAudio.play();
    document.getElementById('hippocrate-neutral')?.classList.add('hidden');
    document.getElementById('hippocrate-sad')?.classList.add('hidden');
    document.getElementById('hippocrate-happy')?.classList.remove('hidden');
    happyTimer = setTimeout(function() {
        happyTimer = null;
        document.getElementById('hippocrate-neutral')?.classList.remove('hidden');
        document.getElementById('hippocrate-happy')?.classList.add('hidden');
    }, 1000);
}

async function wrongAnswer() {
    failAudio.play();
    document.getElementById('hippocrate-neutral')?.classList.add('hidden');
    document.getElementById('hippocrate-happy')?.classList.add('hidden');      // Should not happen but still..
    document.getElementById('hippocrate-sad')?.classList.remove('hidden');
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function waitForNextClick(): Promise<boolean> {
  return new Promise(resolve => {
    const btn = document.querySelector('#next-button');
    if (!btn) {
      throw new Error('Bouton "next" introuvable');
    }

    const handler = () => {
      btn.removeEventListener('click', handler);
      cancelQuizWait = undefined;
      resolve(true);
    };

    cancelQuizWait = () => {
      btn.removeEventListener('click', handler);
      cancelQuizWait = undefined;
      resolve(false);
    };
    btn.addEventListener('click', handler);
  });
}

async function chooseInstance() {
    overlayElement!.replaceChildren(renderer.render({
        children: "coucou"
    }));
}

async function startWindow() {
    videoElement!.src = videoPath('intro.mp4'); //'intro_001.mp4');
    // videoElement?.play();
    setVideoMentions(START_WINDOW_MENTIONS);

    let modalElement;
    overlayElement!.replaceChildren(renderer.render({
        className: 'modal-center', children: [
            { tag: 'button', className: 'modal-btn', children: 'Commencer', onclick: () => {
                modalElement!.remove();
                successAudio.play();
                controlLayer!.classList.remove('hidden');
                setTimeout(() => {
                    videoElement!.src = videoPath('Intro1.mp4'); //'intro_001.mp4');
                    videoElement?.play();
                }, 250);
            } }
        ], hook: (e:HTMLElement) => { modalElement = e; }
    }))
}

let patientCode = "";
let progress = 0;

async function passwordWindow() {
    controlLayer!.classList.add('hidden');
    let inputElement, modalElement;
    overlayElement!.replaceChildren(renderer.render({
        className: 'password-modal', children: [
            { tag: 'div', className: 'password-form', children:[
                { tag: 'label', for:'pwd', className:"password-label", children: 'Mot de passe' },
                { tag: 'input', type: 'password', id:'pwd', className:"password-input", autocomplete:"current-password", required: true, hook: (e) => { inputElement = e as HTMLInputElement; } },
                { tag: 'button', className: 'password-btn', children: 'Ok', onclick: async () => {
                    patientCode = inputElement!.value
                    // console.log("Code", patientCode);
                    if (patientCode == BYPASS_CODE) {
                        // Bypass test
                        successAudio.play();
                        modalElement!.remove();
                        progress = 0;
                        updateProgress(progress);
                        next();
                        return;
                    }
                    const res = await fetch(`/patient/${patientCode}`);
                    if (res.status == 200) {
                        successAudio.play();
                        modalElement!.remove();

                        const patient = await res.json();
                        progress = patient.progress;
                        await fetch(`/patient/${encodeURIComponent(patientCode)}/locale`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ locale }),
                        }).catch(() => undefined);
                        // console.log('progress', progress);
                        updateProgress(progress);
                        
                        next();
                    } else {
                        failAudio.play();
                        inputElement!.value = "";
                    }
                } }
            ]}], hook: (e) => { modalElement = e; }
    }));
}

let infosPatient:any = { /*nom: 'Smith', prenom: 'Jessica'*/ };

async function infoWindow() {
    controlLayer!.classList.add('hidden');
    let modalElement;
    let nomInputElement, prenomInputElement, adnInputElement, emailIntputElement;
    // Champs temporairement retirés : ddnInputElement, ldnInputElement,
    // telInputElement, nssInputElement et profInputElement.
    overlayElement!.replaceChildren(renderer.render({
        className: 'info-modal', children: [
            { tag: 'div', className: 'info-form', children:[
                { className: 'info-line', children: [
                    { tag: 'label', className:"info-label", children: 'Nom' },
                    { tag: 'input', className:"info-input", value: '', hook: (e) => { nomInputElement = e as HTMLInputElement; } },
                ]},
                { className: 'info-line', children: [
                    { tag: 'label', className:"info-label", children: 'Prénom' },
                    { tag: 'input', className:"info-input", value: '', hook: (e) => { prenomInputElement = e as HTMLInputElement; } },
                ]},
                { className: 'info-line', children: [
                    { tag: 'label', className:"info-label", children: 'Année de naissance' },
                    {
                        tag: 'input',
                        className:"info-input",
                        type: 'number',
                        min: 1900,
                        max: new Date().getFullYear(),
                        step: 1,
                        inputmode: 'numeric',
                        autocomplete: 'bday-year',
                        placeholder: 'AAAA',
                        hook: (e) => { adnInputElement = e as HTMLInputElement; }
                    },
                ]},
                // { className: 'info-line', children: [
                //     { tag: 'label', className:"info-label", children: 'Date de naissance' },
                //     { tag: 'input', className:"info-input", hook: (e) => { ddnInputElement = e as HTMLInputElement; } },
                // ]},
                // { className: 'info-line', children: [
                //     { tag: 'label', className:"info-label", children: 'Lieu de naissance' },
                //     { tag: 'input', className:"info-input", hook: (e) => { ldnInputElement = e as HTMLInputElement; } },
                // ]},
                // { className: 'info-line', children: [
                //     { tag: 'label', className:"info-label", children: 'Téléphone' },
                //     { tag: 'input', className:"info-input", hook: (e) => { telInputElement = e as HTMLInputElement; } },
                // ]},
                { className: 'info-line', children: [
                    { tag: 'label', className:"info-label", children: 'Email' },
                    { tag: 'input', className:"info-input", hook: (e) => { emailIntputElement = e as HTMLInputElement; } },
                ]},
                // { className: 'info-line', children: [
                //     { tag: 'label', className:"info-label", children: 'Numéro de sécurité sociale' },
                //     { tag: 'input', className:"info-input", hook: (e) => { nssInputElement = e as HTMLInputElement; } },
                // ]},
                // { className: 'info-line', children: [
                //     { tag: 'label', className:"info-label", children: 'Profession' },
                //     { tag: 'input', className:"info-input", hook: (e) => { profInputElement = e as HTMLInputElement; } },
                // ]},
                { className: 'info-note', children: [
                    { tag: 'span', className: 'info-note-badge', children: 'HDS' },
                    { tag: 'span', className: 'info-note-text', children: 'Données de santé hébergées sur une infrastructure certifiée HDS, avec un niveau de sécurité adapté aux informations médicales.' },
                ]},

                { tag: 'button', className: 'info-btn', children: 'Ok', onclick: async () => {
                    infosPatient = {
                        nom: ANONYMOUS ? 'anonyme' : nomInputElement!.value,
                        prenom: ANONYMOUS ? 'anonyme' : prenomInputElement!.value,
                        adn: adnInputElement!.value,
                        // ddn: ddnInputElement!.value,
                        // ldn: ldnInputElement!.value,
                        // tel: ANONYMOUS ? 'anonyme' : telInputElement!.value,
                        email: ANONYMOUS ? 'anonyme' : emailIntputElement!.value,
                        // nss: ANONYMOUS ? 'anonyme' : nssInputElement!.value,
                        // prof: profInputElement!.value
                    }
                    if (patientCode==BYPASS_CODE) {
                        successAudio.play();
                        modalElement!.remove();
                        next();
                        return;
                    }
                    // console.log(infosPatient);
                    const res = await fetch(`/patient/${patientCode}/info`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify(infosPatient)
                    })
                    // console.log(res);
                    if (res.status == 200) {
                        successAudio.play();
                        modalElement!.remove();
                        next();
                    }
                } }
            ]}], hook: (e) => { modalElement = e; }
    }));
}

async function imcWindow() {
    resetTitle({ immediate: true, clearContent: true });
    controlLayer!.classList.add('hidden');
    let modalElement, poidsElement, tailleElement, imcElement;
    const updateIMC = () => {
        const poids = parseFloat(poidsElement!.value);
        let taille = parseFloat(tailleElement!.value);
        if (taille > 3) {
            // Cm
            taille /= 100;
        }
        const imc = poids / (taille*taille);
        imcElement!.innerText = isNaN(imc) ? '-' : Math.round(imc*10)/10;
    }
    overlayElement!.replaceChildren(renderer.render({
        className: 'imc-modal', children: [
            { className: 'imc-img', children: { tag: 'img', src:'/assets/ui/BalanceIMC.png' } },
            { className: 'imc-form', children:[
                { className: 'imc-line', children: [
                    { tag: 'label', className:"imc-label", children: 'Poids (kg)' },
                    { tag: 'input', className:"imc-input", onkeyup: updateIMC, hook: (e) => { poidsElement = e as HTMLInputElement; } },
                ]},
                { className: 'imc-line', children: [
                    { tag: 'label', className:"imc-label", children: 'Taille (m)' },
                    { tag: 'input', className:"imc-input", onkeyup: updateIMC, hook: (e) => { tailleElement = e as HTMLInputElement; } },
                ]},
                { className: 'imc-line', children: [
                    { tag: 'label', className:"imc-label", children: 'IMC' },
                    { className:"imc-text", hook: (e) => { imcElement = e as HTMLElement; } },
                ]},
                { className: 'info-note', children: [
                    { tag: 'span', className: 'info-note-badge', children: 'HDS' },
                    { tag: 'span', className: 'info-note-text', children: 'Données de santé hébergées sur une infrastructure certifiée HDS, avec un niveau de sécurité adapté aux informations médicales.' },
                ]},

                { tag: 'button', className: 'imc-btn', children: 'Ok', onclick: async () => {

                    // Pour l'instant on n'enregistre pas l'IMC
                    // infosPatient.poids = parseFloat(poidsElement!.value);
                    // infosPatient.taille = parseFloat(tailleElement!.value);
                    // const res = await fetch(`/patient/${patientCode}/info`, {
                    //     method: 'POST',
                    //     headers: {
                    //         'Content-Type': 'application/json'
                    //     },
                    //     body: JSON.stringify(infosPatient)
                    // })

                    successAudio.play();
                    modalElement!.remove();
                    await next();
                } }
            ]}], hook: (e) => { 
                modalElement = e; updateIMC(); 
            }
    }));
}

async function certificate() {
    resetTitle({ immediate: true, clearContent: true });
    controlLayer?.classList.add('hidden');
    
    function toNext() {
        clearTimeout(timer);
        applauseAudio.pause();
        overlayElement!.replaceChildren();
        next(); 
    }

    const timer = setTimeout(toNext, 5000);

    let img, nom, date;
    overlayElement!.replaceChildren(renderer.render({ className: 'certif-canvas', children: [
        { tag: 'img', src: '/assets/ui/certificate.png', className: 'certif-bg', hook: (e) => { img = e; } },
        { className: 'certif-nom', children: `${infosPatient.prenom ?? "John"} ${(infosPatient.nom ?? "Smith").toUpperCase()}`, hook: (e) => { nom = e; } },
        { className: 'certif-date', children: (new Date()).toLocaleDateString(locale === 'en' ? 'en-GB' : 'fr-FR'), hook: (e) => { date = e; } },
        { id: 'baby-controls', children: [
            { className: 'control-buttons', children: [
                { tag: 'button', className: 'control-btn skip-btn', children: { tag: 'i', className: 'fa-solid fa-arrow-right' }, onclick: toNext
            },
            ]},
        ]},
    ], hook: (e) => {
          
        function placeOverlays() {
            const parent = img!.parentElement!;

            // Taille image d’origine
            const IMG_W = 3508, IMG_H = 2480;
            // Coordonnées sur l’image d’origine (à AJUSTER)
            const NOM_X = 1100, NOM_Y = 1000;
            const DATE_X = 500, DATE_Y = 2200;

            // Taille du container (viewport)
            const cw = parent.clientWidth;
            const ch = parent.clientHeight;
            // Ratio du container et de l’image
            const cRatio = cw / ch;
            const iRatio = IMG_W / IMG_H;

            let imgW, imgH, offsetX, offsetY;

            if (cRatio > iRatio) {
            // Le container est plus "large" : l'image prend toute la hauteur
            imgH = ch;
            imgW = ch * iRatio;
            offsetX = (cw - imgW) / 2;
            offsetY = 0;
            } else {
            // Le container est plus "haut" : l'image prend toute la largeur
            imgW = cw;
            imgH = cw / iRatio;
            offsetX = 0;
            offsetY = (ch - imgH) / 2;
            }

            // Place les overlays EN PIXELS dans le container
            nom!.style.left = (offsetX + NOM_X * imgW / IMG_W) + "px";
            nom!.style.top  = (offsetY + NOM_Y * imgH / IMG_H) + "px";

            date!.style.left = (offsetX + DATE_X * imgW / IMG_W) + "px";
            date!.style.top  = (offsetY + DATE_Y * imgH / IMG_H) + "px";
            }

        window.addEventListener('resize', placeOverlays);
        if (img!.complete) placeOverlays();
        else img!.onload = placeOverlays;
    }}));
    applauseAudio.play();
    console.log("Parcours fini !");
    
    if (patientCode==BYPASS_CODE) {
        return;
    }

    const res = await fetch(`/patient/${patientCode}/finished`, {
        method: 'POST',
    })
    console.log(res);
}

async function sponsors() {
    controlLayer?.classList.add('hidden');
    
    overlayElement!.replaceChildren(renderer.render({ className: 'sponsors', children: [

        { className:'sponsor-rows', children: [
            { className:'sponsor-row', children: [
                { tag: 'a', href:'https://mediland.care/', target:'_blank', children: { tag:'img', className:'logo', src:'/assets/logos/logo_mediland.svg' } },
            ]},

            // { className:'sponsor-row', children: [
            //     { tag: 'a', href:'https://www.has-sante.fr/', target:'_blank', children: { tag:'img', className:'logo', src:'/assets/logos/logo_has.png' } },
            //     { tag: 'a', href:'https://liguecontrelobesite.org/', target:'_blank', children: { tag:'img', className:'logo', src:'/assets/logos/logo_ligue.webp' } },
            //     { tag: 'a', href:'https://www.assurance-maladie.ameli.fr/', target:'_blank', children: { tag:'img', className:'logo', src:'/assets/logos/logo_cnam.webp' } },
            // ]},
            // { className:'sponsor-row', children: [
            //     { tag: 'a', href:'https://bodynov.com/', target:'_blank', children: { tag:'img', className:'logo', src:'/assets/logos/logo_bodynov.jpg' } },
            //     { tag: 'a', href:'https://www.bastideleconfortmedical.com/', target:'_blank', children: { tag:'img', className:'logo', src:'/assets/logos/logo_bastide.jpg' } }
            // ]}
        ]}

    ]}));
}

let trackElement:HTMLTrackElement|undefined;

function playVideo(src:string, subtitles:string='') {
    controlLayer!.classList.remove('hidden');
    videoElement!.src = videoPath(src);
    setVideoMentions();
    videoElement!.play();

    resetTitle({ immediate: true, clearContent: true });
    activeTitleCue = undefined;
    symptomNodes.forEach(node => node.remove());
    symptomNodes.clear();
    imageNodes.forEach(node => node.remove());
    imageNodes.clear();

    videoElement!.innerHTML = "";
    trackElement = undefined;
    if (subtitles) {
        const newTrack = document.createElement('track');
        // newTrack.id = 'meta';
        newTrack.kind = 'subtitles';//'metadata';
        newTrack.label = 'Cues';
        newTrack.src = `/assets/content/${locale}/boards/${subtitles}`;
        // console.log(subtitles)
        newTrack.default = true;

        trackElement = newTrack;

        // console.log(newTrack)
        videoElement!.appendChild(newTrack)
        newTrack.track.mode = 'hidden'//'showing'
        
        newTrack.onload = function() {
            // console.log(33)
            const t = newTrack.track;
            // t.mode = 'showing';
            t.oncuechange = scheduleRender
            // console.log(44)
            // console.log('new cues loaded:', t.cues?.length);
            scheduleRender();
        }
        // console.log(track);
        // track.mode = 'showing';
        // track.oncuechange = function() {
        //     console.log("coucou")
        // }
    }
}

// Boards
function getActivePayloads() {
    if (!trackElement) return [];
    const tt = Array.from(videoElement!.textTracks).find(t => t === trackElement!.track);
    if (!tt) return [];
    const active = Array.from(tt.activeCues || []);
    const payloads = [];
    for (const cue of active) {
        try {
            payloads.push({ cue, data: JSON.parse((cue as any).text) });
        } catch { /* ignorer les cues non-JSON */ }
    }
    return payloads;
}

function partitionPayloads(payloads:any) {
    const byLayer = new Map();
    for (const p of payloads) {
        const layer = p.data.layer || 'default';
        if (!byLayer.has(layer)) byLayer.set(layer, []);
        byLayer.get(layer).push(p);
    }
    return byLayer;
}

// Rendu “idempotent” : on reconstruit la vue de chaque layer depuis zéro
function render() {
    const payloads = getActivePayloads();
    const byLayer = partitionPayloads(payloads);

    const titleEntries = byLayer.get('title') || [];
    if (titleEntries.length) {
        const current = titleEntries[titleEntries.length - 1];
        if (activeTitleCue !== current.cue) {
            activeTitleCue = current.cue;
            const data = current.data ?? {};
            const rawContent = (data.render ?? data.content ?? data.children ?? data.text ?? data.title);
            if (rawContent !== undefined) {
                showTitle(rawContent);
            } else {
                showTitle('');
            }
        }
    } else if (activeTitleCue) {
        activeTitleCue = undefined;
        resetTitle({ immediate: true, clearContent: true });
    }

    // SYMPTÔMES (liste par group)
    const sLayer = document.querySelector('[data-layer="symptoms"]') as HTMLElement | null;
    if (sLayer) {
        const symptoms = (byLayer.get('symptoms') || []).map((p:any) => p.data);
        const groups = new Map<string, any[]>();
        for (const it of symptoms) {
            const g = String(it.group || 'default');
            if (!groups.has(g)) groups.set(g, []);
            groups.get(g)!.push(it);
        }
        const nextKeys = new Set<string>();
        for (const [group, items] of groups) {
            const key = group;
            const signature = JSON.stringify(items.map((it:any) => ({
                title: it.title || '',
                text: it.text || ''
            })));
            nextKeys.add(key);

            let card = symptomNodes.get(key);
            if (!card) {
                card = createSymptomCard(items, key);
                card.dataset.symptomSignature = signature;
                symptomNodes.set(key, card);
                triggerFadeIn(card);
            } else if (card.dataset.symptomSignature !== signature) {
                populateSymptomCard(card, items);
                card.dataset.symptomSignature = signature;
                triggerFadeIn(card);
            }
            sLayer.appendChild(card);
        }
        for (const [key, node] of Array.from(symptomNodes.entries())) {
            if (!nextKeys.has(key)) {
                node.remove();
                symptomNodes.delete(key);
            }
        }
    }

    // IMAGES (on peut autoriser plusieurs images actives)
    const iLayer = document.querySelector('[data-layer="image"]') as HTMLElement | null;
    if (iLayer) {
        const figures = (byLayer.get('image') || []).map((p:any) => p.data);
        const hasFigures = figures.length > 0;
        const nextFigureKeys = new Set<string>();
        figures.forEach((fig:any, index:number) => {
            const key = String(fig.id ?? fig.key ?? fig.src ?? `figure-${index}`);
            const signature = JSON.stringify(fig);
            nextFigureKeys.add(key);

            let figureEl = imageNodes.get(key);
            if (!figureEl) {
                figureEl = createFigureElement(fig, key);
                figureEl.dataset.figureSignature = signature;
                imageNodes.set(key, figureEl);
                triggerFadeIn(figureEl);
            } else if (figureEl.dataset.figureSignature !== signature) {
                updateFigureElement(figureEl, fig);
                figureEl.dataset.figureSignature = signature;
                triggerFadeIn(figureEl);
            }
            iLayer.appendChild(figureEl);
        });
        for (const [key, node] of Array.from(imageNodes.entries())) {
            if (!nextFigureKeys.has(key)) {
                node.remove();
                imageNodes.delete(key);
            }
        }
        if (hasFigures) {
            if (!titleSuppressedByImage) {
                titleSuppressedByImage = true;
                hideTitle(true);
            }
        } else if (titleSuppressedByImage) {
            titleSuppressedByImage = false;
            if (activeTitleCue && lastTitleContent !== undefined) {
                showTitle(undefined, { remind: false });
            }
        }
    }
}

// Debounce léger pour éviter de rerendre trop souvent si tu écoutes timeupdate
let rafId:any = null;
function scheduleRender() {
    console.log("scheduleRender() ");
    if (rafId) return;
    rafId = requestAnimationFrame(() => { rafId = null; render(); });
}



// async function to_pdsoin() {
//     playVideo('navTo2nd_001.mp4')
// }

// async function to_spe() {
//     playVideo('navToSpe_001.mp4');
// }

async function sleep(time:number) { return new Promise( (resolve) => { setTimeout(resolve, time); }) }

type fun = ()=>void;
async function bypass(progress_cap:number, f:fun) {
    if (progress > progress_cap) {
        await next();
    }
    return f;
}

let nextSequence=0;
const playSequence = [
    // { progress: 0, f:chooseInstance },
    { progress: 0, f:startWindow },
    { progress: 0, f:async () => { playVideo('Reception1.mp4'); } },       // navTo2nd_001.mp4
    { progress: 0, f:passwordWindow },
    { progress: 0, f:fastForward },
    // transition
    { progress: 0, f:async () => { playVideo('Transition1.mp4'); } },

    { progress: 0, f:async () => { playVideo('Secr1.mp4'); } },       // navTo2nd_001.mp4
    { progress: 0, f:infoWindow },
    { progress: 0, f:async () => { playVideo('Transition2.mp4'); } },

    { progress: 0, f:async () => { playVideo('Spe1.mp4', 'spe/spe_1.vtt'); } },     // ('navToSpe_001.mp4', 'spe/spe_1.vtt'
    { progress: 0, f:imcWindow },
    { progress: 0, f:async () => { playVideo('Spe2.mp4', 'spe/spe_2.vtt'); } },
    { progress: 5, f:async () => { playVideo('Spe3.mp4', 'spe/spe_3.vtt'); } },
    { progress: 5, f:async () => { await playQuiz('spe', 0, 5); } },
    { progress: 10, f:async () => { playVideo('Spe4.mp4', 'spe/spe_4.vtt'); } },
    { progress: 10, f:async () => { await playQuiz('spe', 5, 10); } },
    { progress: 15, f:async () => { playVideo('Spe5.mp4', 'spe/spe_5.vtt'); } },
    { progress: 15, f:async () => { await playQuiz('spe', 10, 15); } },
    { progress: 15, f:async () => { playVideo('Spe6.mp4', 'spe/spe_6.vtt'); } },
    { progress: 24, f:async () => { await playQuiz('spe', 15, 24); } },
    { progress: 15, f:async () => { playVideo('Spe7.mp4', ''); } },
    // transition
    { progress: 0, f:async () => { playVideo('Transition1.mp4'); } },

    { progress: 24, f:async () => { playVideo('Surg1.mp4', 'surg/surg_1.vtt'); } },         // speToChir_001.mp4', 'surg/surg_1.vtt'
    { progress: 28, f:async () => { await playQuiz('surg', 25-25, 28-25); } },
    { progress: 28, f:async () => { playVideo('Surg2.mp4', 'surg/surg_2.vtt'); } },
    { progress: 36, f:async () => { await playQuiz('surg', 28-25, 36-25); } },
    { progress: 36, f:async () => { playVideo('Surg3.mp4', 'surg/surg_3.vtt'); } },
    { progress: 36, f:async () => { playVideo('Surg4.mp4', 'surg/surg_4.vtt'); } },
    { progress: 36, f:async () => { playVideo('Surg5.mp4', 'surg/surg_5.vtt'); } }, 
    { progress: 46, f:async () => { await playQuiz('surg', 36-25, 46-25); } },
    // transition
    { progress: 0, f:async () => { playVideo('Transition2.mp4'); } },

    { progress: 46, f:async () => { playVideo('Diet1.mp4', 'diet/diet_1.vtt'); } },        // 'chirToDiet_001.mp4', 'diet/diet_1.vtt'
    { progress: 51, f:async () => { await playQuiz('diet', 46-46, 51-46); } },
    { progress: 51, f:async () => { playVideo('Diet2.mp4', 'diet/diet_2.vtt'); } },
    { progress: 59, f:async () => { await playQuiz('diet', 51-46, 59-46); } },
    // transition
    { progress: 0, f:async () => { playVideo('Transition1.mp4'); } },

    { progress: 59, f:async () => { playVideo('Psy1.mp4', 'psy/psy_1.vtt'); } },       // 'dietToPsy_001.mp4', 'psy/psy_1.vtt'
    { progress: 64, f:async () => { await playQuiz('psy', 59-59, 64-59); } },
    // transition
    { progress: 0, f:async () => { playVideo('Transition2.mp4'); } },

    { progress: 64, f:async () => { playVideo('Anesth1.mp4', 'anesth/anesth_1.vtt'); } },       // 'psyToAnesth_001.mp4'
    { progress: 64, f:async () => { playVideo('Anesth2.mp4', 'anesth/anesth_2.vtt'); } },      
    { progress: 64, f:async () => { playVideo('Anesth3.mp4', 'anesth/anesth_3.vtt'); } },      
    { progress: 71, f:async () => { await playQuiz('anesth', 64-64, 71-64); } },

    // { progress: 71, f:async () => { playVideo('anesthToMeeting_001.mp4'); } },

    { progress: 71, f:certificate },
    
    { progress: 100, f:async () => { playVideo('Reception_fin1.mp4'); } },
    { progress: 100, f:sponsors },
];

async function fastForward() {
    while (playSequence[nextSequence].progress < progress) {
        nextSequence++;
    }
    await playSequence[nextSequence++].f();
}

let sequenceTransitionInProgress = false;

async function advanceSequence(allowReentrant = false) {
    if (quizActive && !allowReentrant) return;

    const ownsTransitionLock = !sequenceTransitionInProgress;
    if (!ownsTransitionLock && !allowReentrant) return;
    if (ownsTransitionLock) sequenceTransitionInProgress = true;

    try {
        stopQuizAudio();
        if (!videoElement?.paused) {
            videoElement?.pause();
        }
        if (nextSequence < playSequence.length) {
            await (playSequence[nextSequence++].f());
        } else {
            console.log('THE END...');
        }
    } finally {
        if (ownsTransitionLock) sequenceTransitionInProgress = false;
    }
}

async function next() {
    await advanceSequence();
}

async function main() {
    renderLayout();

    document.body.appendChild(createLanguageSwitcher(locale, async (nextLocale) => {
        if (patientCode && patientCode !== BYPASS_CODE) {
            await fetch(`/patient/${encodeURIComponent(patientCode)}/locale`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ locale: nextLocale }),
            }).catch(() => undefined);
        }
        window.location.reload();
    }, t('Choisir la langue')));

    videoElement?.addEventListener('ended', next);

    await next();
}

main();
